import { z } from "zod";
import {
  DbBool,
  IsoDateTime,
  LocalIdSchema,
  PositionSchema,
  jsonColumn,
} from "./common.ts";

export const ResearchCoverageSchema = z.enum([
  "rich",
  "moderate",
  "sparse",
  "little_documented",
]);
export type ResearchCoverage = z.infer<typeof ResearchCoverageSchema>;

export const ResearchConfidenceSchema = z.enum(["high", "medium", "low"]);
export type ResearchConfidence = z.infer<typeof ResearchConfidenceSchema>;

export const ResearchCategoryOriginSchema = z.enum([
  "builtin",
  "discovered",
  "manual",
]);
export type ResearchCategoryOrigin = z.infer<typeof ResearchCategoryOriginSchema>;

/** Open vocabulary — not an enum. Short lowercase-ish tag. */
export const ResearchCategoryNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .transform((s) => s.toLowerCase().replace(/\s+/g, "_"));

export const ResearchClaimSchema = z.object({
  category: ResearchCategoryNameSchema,
  text: z.string().trim().min(1).max(500),
  detail: z
    .union([z.string().trim().min(1).max(2_000), z.null()])
    .optional()
    .transform((v) => (v === undefined ? null : v)),
  confidence: ResearchConfidenceSchema,
  sources: z.array(z.string().trim().min(1).max(500)).min(1).max(20),
  conflict: z
    .union([z.string().trim().min(1).max(1_000), z.null()])
    .optional()
    .transform((v) => (v === undefined ? null : v)),
});
export type ResearchClaim = z.infer<typeof ResearchClaimSchema>;

export const ResearchSubjectSchema = z.object({
  track: z.string().trim().min(1).max(300),
  artist: z.string().trim().min(1).max(300),
});

export const ResearchPayloadSchema = z.object({
  subject: ResearchSubjectSchema,
  coverage: ResearchCoverageSchema,
  coverage_note: z.string().trim().min(1).max(1_000),
  claims: z.array(ResearchClaimSchema).min(1).max(20),
  suggested_arc: z.array(ResearchCategoryNameSchema).max(20).default([]),
  gaps: z.array(z.string().trim().min(1).max(500)).max(20).default([]),
});
export type ResearchPayload = z.infer<typeof ResearchPayloadSchema>;

/**
 * Follow-up pastes after the first ingest — same claims shape, but top-level
 * envelope fields may be omitted (filled from the idea / last ingest).
 */
export const ResearchPayloadContinuationSchema = z.object({
  subject: ResearchSubjectSchema.optional(),
  coverage: ResearchCoverageSchema.optional(),
  coverage_note: z.string().trim().min(1).max(1_000).optional(),
  claims: z.array(ResearchClaimSchema).min(1).max(20),
  suggested_arc: z.array(ResearchCategoryNameSchema).max(20).optional().default([]),
  gaps: z.array(z.string().trim().min(1).max(500)).max(20).optional().default([]),
});
export type ResearchPayloadContinuation = z.infer<typeof ResearchPayloadContinuationSchema>;

export const ResearchPromptModeSchema = z.enum(["initial", "continue"]);
export type ResearchPromptMode = z.infer<typeof ResearchPromptModeSchema>;

export const ResearchIngestRequestSchema = z.object({
  pasted: z.string().trim().min(2).max(200_000),
});
export type ResearchIngestRequest = z.infer<typeof ResearchIngestRequestSchema>;

export const ResearchCategorySchema = z.object({
  id: LocalIdSchema,
  name: z.string().min(1).max(40),
  question: z.string().min(1).max(500),
  active: DbBool,
  origin: ResearchCategoryOriginSchema,
  position: PositionSchema,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type ResearchCategory = z.infer<typeof ResearchCategorySchema>;

export const ResearchCategoryUpdateSchema = z
  .object({
    name: ResearchCategoryNameSchema,
    question: z.string().trim().min(1).max(500),
    active: z.boolean(),
  })
  .partial();
export type ResearchCategoryUpdate = z.infer<typeof ResearchCategoryUpdateSchema>;

export const ResearchCategoryCreateSchema = z.object({
  name: ResearchCategoryNameSchema,
  question: z.string().trim().min(1).max(500),
  active: z.boolean().default(true),
});
export type ResearchCategoryCreate = z.infer<typeof ResearchCategoryCreateSchema>;

export const ResearchNoteSchema = z.object({
  id: LocalIdSchema,
  coverage: ResearchCoverageSchema,
  coverage_note: z.string(),
  gaps: jsonColumn(z.array(z.string())),
  note_dismissed: DbBool,
  created_at: IsoDateTime,
});
export type ResearchNote = z.infer<typeof ResearchNoteSchema>;

export const ResearchPromptResponseSchema = z.object({
  prompt: z.string().min(1),
  anchors: z.array(z.string()),
});
export type ResearchPromptResponse = z.infer<typeof ResearchPromptResponseSchema>;

export const ResearchTemplateSchema = z.object({
  template: z.string().min(1).max(50_000),
});
export type ResearchTemplate = z.infer<typeof ResearchTemplateSchema>;
